"use client";

/**
 * Панель в СТРОКЕ ЗАГОЛОВКА экрана: уведомления, чат, настройки, профиль.
 *
 * ЗАЧЕМ. Максим 28.08.2026 показал образец: «смотри где у них находятся
 * настройки, уведомления и иконка профиля» — правый верхний угол, одной
 * панелью, рядом с именем и должностью. У нас всё это жило внизу левого рельса.
 *
 * ⚠️ ОНА В ПОТОКЕ, А НЕ ПОВЕРХ. Первая версия висела абсолютом в углу контента,
 * и Максим сразу это увидел: «там это находится прямо в теле страницы, а ты
 * сделал плавающее». Поэтому панель рисует сама шапка экрана (`TabHeader`) —
 * она встаёт последним элементом ряда контролов и переносится вместе с ними на
 * узком экране. Никаких резервов места и перекрытий.
 *
 * ⚠️ ОДНА НА ЭКРАН. `TabHeader` показывает её только вместе с заголовком:
 * внутри отдела шапок две (страница отдела + модуль), и без этого условия
 * панель задвоилась бы. Экраны со СВОЕЙ шапкой (главная, инструкции,
 * настройки, HR) ставят `<TopPanel />` сами.
 */

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { MessagesSquare } from "lucide-react";

import { useAuth } from "@/components/auth-provider";
import { useTheme } from "@/components/theme-provider";
import { NotificationsButton } from "@/components/notifications-button";
import { UserAvatar } from "@/components/ui/user-avatar";
import { IconChevronDown, IconGear, IconMoon, IconSun, IconUser } from "@/components/ui/icons";
import { PortalSearch } from "@/components/portal-search";
import { LogOut } from "lucide-react";
import { useOutsideClick } from "@/lib/use-outside-click";
import { glassPillStyle } from "@/lib/theme";
import { deptColorOf } from "@/lib/dept-colors";
import { api } from "@/lib/api";
import { useApiQuery } from "@/lib/use-api-query";
import type { HrEmployee } from "@/lib/hr";
import type { Department } from "@/lib/types";
import { Z } from "@/lib/z";

// Короткие подписи: в панели под должность 124 px, и «Администратор платформы»
// обрывался многоточием. Полные названия остаются в профиле, здесь — как на
// образце («Основатель»), одним словом.
const ROLE_TITLE: Record<string, string> = {
  employee: "Сотрудник",
  department_head: "Руководитель",
  platform_admin: "Администратор",
};

// ── Одна панель на экран ────────────────────────────────────────────────────
//
// Шапок с заголовком на странице может быть НЕСКОЛЬКО: «Настройки модулей»
// печатает свой заголовок, а выбранный раздел — свой. Панель рисовалась в
// каждой (Максим, 28.08.2026: «зачем дважды отображать одно и то же?»).
//
// Контекстом это не решается: шапки не вложены друг в друга, а стоят рядом —
// вторая про первую ничего не знает. Поэтому держим один общий «замок»: кто
// смонтировался первым, тот и рисует панель; остальные молчат. Первой всегда
// монтируется верхняя — React обходит дерево сверху вниз.
//
// Владелец ушёл со страницы — замок освобождается, и панель забирает следующая
// шапка. Иначе при переходе между разделами экран остался бы вовсе без панели.
let owner: object | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((fn) => fn());

export function TopPanel({ className = "" }: { className?: string }) {
  const { user, logout } = useAuth();
  const { mode, setMode } = useTheme();
  // Цвет отдела для плейсхолдера аватара — из того же справочника, что красит
  // отделы везде. Запрос кэша не имеет, но и панель монтируется один раз.
  const deptQ = useApiQuery<Department[]>(() => api.getDepartments(), []);
  // Замок «одна панель на экран» — см. комментарий выше.
  const token = useRef<object>({});
  const [mine, setMine] = useState(false);
  useEffect(() => {
    const self = token.current;
    const check = () => {
      if (owner === null) { owner = self; notify(); }
      setMine(owner === self);
    };
    listeners.add(check);
    check();
    return () => {
      listeners.delete(check);
      if (owner === self) { owner = null; notify(); }
    };
  }, []);

  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useOutsideClick([notifRef], () => setNotifOpen(false), notifOpen);
  useOutsideClick([menuRef], () => setMenuOpen(false), menuOpen);

  // Своя карточка сотрудника — для пункта «Профиль» в меню. Ссылка ведёт прямо
  // в карточку (/team/<id>), а не в раздел «Команда»: у людей без права на
  // список там пусто, а свой профиль открыт всегда (Максим, 04.09.2026).
  const мояКарточка = useApiQuery<HrEmployee | null>(() => api.hrMyEmployee(), []);
  const мойПрофиль = мояКарточка.data?.id ? `/team/${мояКарточка.data.id}` : "/profile";

  // Непрочитанное для значка чата: панель считает и рассылает событием.
  const [непрочитано, setНепрочитано] = useState(0);
  const [разбивка, setРазбивка] = useState({ chat: 0, tasks: 0, requests: 0 });
  useEffect(() => {
    function приход(e: Event) {
      const c = (e as CustomEvent<{ chat: number; tasks: number; requests: number }>).detail;
      if (!c) return;
      setРазбивка(c);
      // ⚠️ ТОЛЬКО СООБЩЕНИЯ (Максим, 04.09.2026). На кружке бейдж складывал
      // сообщения, задачи и доработки — там он был общим на всю панель. Здесь
      // значок именно чата, и число рядом с ним должно означать письма, иначе
      // человек идёт в чат за «31», а там два непрочитанных.
      setНепрочитано(c.chat || 0);
    }
    window.addEventListener("yuc:unread", приход);
    return () => window.removeEventListener("yuc:unread", приход);
  }, []);

  if (!user || !mine) return null;

  const canOpenSettings = user.role === "platform_admin";
  // ⚠️ `glass-flat` — ОТКАЗ ОТ ОБЩЕЙ ТЕНИ КНОПОК. В тёмной теме globals.css
  // вешает на каждую кнопку блик сверху и тень-глубину; на значках панели это
  // читалось как коробка вокруг них — ровно то свечение, которое Максим убрал
  // с панелей 27.08.2026 и попросил убрать здесь (01.09.2026). Класс —
  // штатный opt-out из того же правила (`:not(.glass-flat)`).
  const btn =
    "glass-flat inline-flex h-9 w-9 items-center justify-center rounded-[var(--radius-md)] text-[var(--textMuted)] transition hover:text-[var(--text)]";
  const rowClass =
    "group flex h-9 w-full items-center gap-2 rounded-[var(--radius-md)] px-2 text-left text-[12px] font-semibold text-[var(--textMuted)] transition hover:text-[var(--text)] glass-nav";

  return (
    // ⚠️ БЕЗ КОРОБКИ. Максим, 28.08.2026: «на образце, как видишь, нету рамок».
    // Первая версия была панелью со стеклом, рамкой и тенью — на образце значки
    // стоят прямо на фоне страницы, и разделителя между ними тоже нет.
    <div data-top-panel className={`flex shrink-0 items-center gap-1 ${className}`}>
      {/* ⚠️ ПОИСК — ОДИН ЭЛЕМЕНТ, который сам раскрывается (Максим, 03.09.2026).
          Была кнопка, открывавшая отдельную палитру посреди экрана: два разных
          элемента там, где человек ждёт один растущий. Логика и список находок
          живут в самом компоненте. */}
      <PortalSearch className="mr-1 hidden md:block" />

      <div ref={notifRef} className="relative">
        <NotificationsButton collapsed open={notifOpen} onOpenChange={setNotifOpen} containerRef={notifRef} inline />
      </div>

      {/* Чат. ⚠️ ЕДИНСТВЕННЫЙ ВХОД В ПАНЕЛЬ (Максим, 04.09.2026): кружок в углу
          убран, поэтому здесь же счётчик непрочитанного — иначе новые сообщения
          стало бы не видно. Считает по-прежнему панель, сюда число приезжает
          событием «yuc:unread»: опрос сервера остаётся один. */}
      <button
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent("yuc:open-chat"))}
        data-tooltip={непрочитано > 0
          ? `Непрочитанных сообщений: ${непрочитано}${разбивка.tasks ? ` · задач в работе: ${разбивка.tasks}` : ""}${разбивка.requests ? ` · доработок: ${разбивка.requests}` : ""}`
          : "Чат и задачи"}
        aria-label={непрочитано > 0 ? `Чат и задачи, непрочитанных сообщений: ${непрочитано}` : "Чат и задачи"}
        className={`${btn} relative`}
      >
        <MessagesSquare size={17} />
        {непрочитано > 0 && (
          <span
            className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold leading-none text-white"
            style={{ background: "var(--danger)" }}
          >
            {непрочитано > 99 ? "99+" : непрочитано}
          </span>
        )}
      </button>

      {canOpenSettings && (
        <Link href="/settings" data-tooltip="Настройки" aria-label="Настройки" className={btn}>
          <IconGear size={17} />
        </Link>
      )}

      {/* Тема — ОДНОЙ кнопкой, как на экране входа: значок показывает, куда
          переключишь (Максим, 28.08.2026). В рельсе стояла пара «солнце/луна»
          в плашке — она занимала строку и повторяла выбор, который и так виден
          по самой теме. Место — рядом с шестерёнкой, по просьбе. */}
      <button
        type="button"
        onClick={() => setMode(mode === "dark" ? "light" : "dark")}
        data-tooltip={mode === "dark" ? "Светлая тема" : "Тёмная тема"}
        aria-label={mode === "dark" ? "Светлая тема" : "Тёмная тема"}
        className={btn}
      >
        {mode === "dark" ? <IconSun size={17} /> : <IconMoon size={17} />}
      </button>

      <div ref={menuRef} className="relative">
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          data-open={menuOpen ? "1" : undefined}
          style={menuOpen ? { translate: "none" } : undefined}
          className="glass-flat group flex items-center gap-2 rounded-[var(--radius-md)] py-1 pl-1 pr-1.5 text-left transition"
        >
          <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full">
            <UserAvatar
              name={user.fullName || user.email || "?"}
              avatarUrl={user.profile?.avatarUrl}
              userId={user.id}
              size={26}
              fillBg
              color={deptColorOf(deptQ.data ?? [], user.department ?? "")}
            />
          </span>
          {/* Имя и должность — как на образце. На узком экране остаётся аватар:
              двухстрочная подпись в переносимом ряду контролов ломает строку. */}
          <span className="hidden min-w-0 md:block">
            <span className="block max-w-[124px] truncate text-[12px] font-semibold leading-tight text-[var(--text)]">
              {user.fullName || user.email}
            </span>
            <span className="block max-w-[124px] truncate text-[11px] leading-tight text-[var(--textMuted)]">
              {ROLE_TITLE[user.role] ?? user.role}
            </span>
          </span>
          <IconChevronDown
            size={14}
            className={`shrink-0 text-[var(--textMuted)] transition-transform ${menuOpen ? "rotate-180" : ""}`}
          />
        </button>

        {menuOpen && (
          <div
            className="yuc-unfurl-tr absolute right-0 top-[calc(100%+8px)] w-[214px] overflow-hidden rounded-[var(--radius-lg)] p-1.5"
            style={{ ...glassPillStyle(mode === "dark"), background: "var(--surfaceElevated)", zIndex: Z.FAB }}
          >
            {canOpenSettings && (
              <Link href="/settings" onClick={() => setMenuOpen(false)} className={rowClass}>
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                  style={{ background: "color-mix(in srgb, var(--textMuted) 12%, transparent)" }}
                >
                  <IconGear size={16} />
                </span>
                <span className="flex-1 truncate">Настройки</span>
              </Link>
            )}
            <Link href={мойПрофиль} onClick={() => setMenuOpen(false)} className={rowClass}>
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                style={{ background: "color-mix(in srgb, var(--primary) 12%, transparent)", color: "var(--primary)" }}
              >
                <IconUser size={16} />
              </span>
              <span className="flex-1 truncate">Профиль</span>
            </Link>
            <div className="my-1.5 h-px" style={{ background: "color-mix(in srgb, var(--text) 10%, transparent)" }} />
            <button
              type="button"
              onClick={() => { setMenuOpen(false); void logout(); }}
              className={`${rowClass} text-[var(--danger)] hover:text-[var(--danger)]`}
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
                style={{ background: "color-mix(in srgb, var(--danger) 12%, transparent)", color: "var(--danger)" }}
              >
                <LogOut size={16} />
              </span>
              <span className="flex-1 truncate">Выйти</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
